import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Shield, Truck, Receipt, FileText } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUOLI = ['admin', 'tecnico', 'operatore', 'commerciale'];
const COLORI_PREIMPOSTATI = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

export default function UtentiPage() {
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUtente, setEditingUtente] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: '',
    ruolo: 'operatore',
    telefono: '',
    colore: '#3b82f6',
    attivo: true,
    permessi_documenti: {
      ddt: false,
      fattura: false,
      nota_credito: false
    }
  });

  useEffect(() => {
    fetchUtenti();
  }, []);

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUtenti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUtente) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.email; // Can't change email
        await axios.put(`${API}/users/${editingUtente.id}`, updateData);
        toast.success('Utente aggiornato');
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success('Utente creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchUtenti();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleEdit = (utente) => {
    setEditingUtente(utente);
    setFormData({
      email: utente.email,
      password: '',
      nome: utente.nome,
      cognome: utente.cognome,
      ruolo: utente.ruolo,
      telefono: utente.telefono || '',
      colore: utente.colore || '#3b82f6',
      attivo: utente.attivo ?? true,
      permessi_documenti: utente.permessi_documenti || {
        ddt: false,
        fattura: false,
        nota_credito: false
      }
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Utente eliminato');
      fetchUtenti();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const resetForm = () => {
    setEditingUtente(null);
    setFormData({
      email: '',
      password: '',
      nome: '',
      cognome: '',
      ruolo: 'operatore',
      telefono: '',
      colore: '#3b82f6',
      attivo: true,
      permessi_documenti: {
        ddt: false,
        fattura: false,
        nota_credito: false
      }
    });
  };

  const filteredUtenti = utenti.filter(u => 
    u.nome?.toLowerCase().includes(search.toLowerCase()) ||
    u.cognome?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="utenti-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Utenti</h1>
          <p className="text-muted-foreground text-sm mt-1">{utenti.length} utenti registrati</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono uppercase tracking-wider" data-testid="new-utente-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader>
              <DialogTitle className="font-mono uppercase tracking-wider">
                {editingUtente ? 'Modifica Utente' : 'Nuovo Utente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="rounded-none"
                    required
                    disabled={!!editingUtente}
                    data-testid="utente-email"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">
                    Password {editingUtente ? '(lascia vuoto per non modificare)' : '*'}
                  </Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="rounded-none"
                    required={!editingUtente}
                    data-testid="utente-password"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase">Nome *</Label>
                    <Input
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="rounded-none"
                      required
                      data-testid="utente-nome"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Cognome *</Label>
                    <Input
                      value={formData.cognome}
                      onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                      className="rounded-none"
                      required
                      data-testid="utente-cognome"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Ruolo *</Label>
                  <Select 
                    value={formData.ruolo} 
                    onValueChange={(value) => setFormData({...formData, ruolo: value})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="utente-ruolo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {RUOLI.map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Telefono</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="rounded-none"
                    data-testid="utente-telefono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Colore Calendario</Label>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {COLORI_PREIMPOSTATI.map((colore) => (
                      <button
                        key={colore}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                          formData.colore === colore ? 'border-foreground scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: colore }}
                        onClick={() => setFormData({...formData, colore})}
                      />
                    ))}
                    <Input
                      type="color"
                      value={formData.colore}
                      onChange={(e) => setFormData({...formData, colore: e.target.value})}
                      className="w-8 h-8 p-0 border-0 rounded-full cursor-pointer"
                      title="Colore personalizzato"
                    />
                  </div>
                </div>

                {/* Permessi Documenti - Solo per non-admin */}
                {formData.ruolo !== 'admin' && (
                  <Card className="rounded-md border-cyan-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-mono uppercase flex items-center gap-2">
                        <Shield className="h-4 w-4 text-cyan-500" />
                        Permessi Documenti
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground mb-3">
                        Abilita la creazione di DDT, Fatture e Note di Credito per questo utente.
                        Preventivi e Offerte sono sempre disponibili.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {/* DDT */}
                        <div className={`p-3 rounded-md border transition-colors ${formData.permessi_documenti.ddt ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Truck className="h-4 w-4 text-amber-500" />
                              <span className="text-xs font-mono uppercase">DDT</span>
                            </div>
                            <Switch
                              checked={formData.permessi_documenti.ddt}
                              onCheckedChange={(checked) => setFormData({
                                ...formData, 
                                permessi_documenti: {...formData.permessi_documenti, ddt: checked}
                              })}
                              data-testid="permesso-ddt"
                            />
                          </div>
                        </div>

                        {/* Fattura */}
                        <div className={`p-3 rounded-md border transition-colors ${formData.permessi_documenti.fattura ? 'border-emerald-500 bg-emerald-500/10' : 'border-border'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Receipt className="h-4 w-4 text-emerald-500" />
                              <span className="text-xs font-mono uppercase">Fattura</span>
                            </div>
                            <Switch
                              checked={formData.permessi_documenti.fattura}
                              onCheckedChange={(checked) => setFormData({
                                ...formData, 
                                permessi_documenti: {...formData.permessi_documenti, fattura: checked}
                              })}
                              data-testid="permesso-fattura"
                            />
                          </div>
                        </div>

                        {/* Nota Credito */}
                        <div className={`p-3 rounded-md border transition-colors ${formData.permessi_documenti.nota_credito ? 'border-red-500 bg-red-500/10' : 'border-border'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Receipt className="h-4 w-4 text-red-500" />
                              <span className="text-xs font-mono uppercase">Nota Cr.</span>
                            </div>
                            <Switch
                              checked={formData.permessi_documenti.nota_credito}
                              onCheckedChange={(checked) => setFormData({
                                ...formData, 
                                permessi_documenti: {...formData.permessi_documenti, nota_credito: checked}
                              })}
                              data-testid="permesso-nota-credito"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                  Annulla
                </Button>
                <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-utente-btn">
                  {editingUtente ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca utente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="search-utenti"
        />
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Colore</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Ruolo</th>
              <th className="hidden md:table-cell">Telefono</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredUtenti.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted-foreground py-8">
                  Nessun utente trovato
                </td>
              </tr>
            ) : (
              filteredUtenti.map((utente) => (
                <tr key={utente.id}>
                  <td>
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-background shadow-sm mx-auto" 
                      style={{ backgroundColor: utente.colore || '#3b82f6' }}
                      title={utente.colore || '#3b82f6'}
                    />
                  </td>
                  <td className="font-medium">{utente.nome} {utente.cognome}</td>
                  <td className="font-mono text-sm">{utente.email}</td>
                  <td>
                    <Badge variant="outline" className="rounded-none uppercase text-xs font-mono">
                      {utente.ruolo}
                    </Badge>
                  </td>
                  <td className="hidden md:table-cell">{utente.telefono || '-'}</td>
                  <td>
                    <Badge className={`rounded-none ${utente.attivo ? 'bg-success/20 text-success border border-success/30' : 'bg-muted text-muted-foreground'}`}>
                      {utente.attivo ? 'Attivo' : 'Disattivato'}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => handleEdit(utente)} data-testid={`edit-utente-${utente.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => handleDelete(utente.id)} data-testid={`delete-utente-${utente.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
